/**
 * Route metadata for the shell. The route → screen-component wiring is added as
 * screens are ported (see screens/registry); this file holds the stable routing
 * data: the canonical route id list and the read-level capability guards.
 *
 * Ported verbatim from the prototype's ROUTE_TABLE keys + ROUTE_READ_PERM.
 */

/** Every navigable route id in the app (the ROUTE_TABLE keys). */
export const ROUTE_IDS = [
  "signin", "dashboard", "eventday", "events", "templates", "templates.detail",
  "ticket-groups", "ticket-groups.edit", "ticket-types", "ticket-types.edit",
  "questions", "questions.groups", "questions.edit", "questions.group.edit",
  "promos", "promos.edit",
  "services", "services.edit", "services.categories", "services.category.edit", "services.packages", "services.package.edit",
  "products", "products.edit",
  /* LOCAL PATCH (platform side, 2026-09-11 customers + automatic-emails cut): the route ids
     `people.person` and `roles` are HELD BACK — not reversed. Both are screens exported by
     the Gold Master `screens/people/StaffAndRoles.tsx`, which this cut excludes (it needs the
     api-client `getStaffAvailability` operation, not on main), so they would resolve to the
     honest "not yet ported" stub; and `roles` gates on `admin.super`, a capability no contract
     catalog declares. They return verbatim with the Staff-and-Roles cut, together with the
     Roles rail item in chrome-rail.tsx and their ROUTE_READ_PERM entries below. */
  "people",
  "staff", "staff.edit", "staff.groups", "staff.group.edit",
  "resources", "resources.edit", "resources.groups", "resources.group.edit",
  "queues", "appointments", "appointment.manage", "appointment.detail",
  "queues.console", "queues.health", "queues.dashboard", "queues.boards", "queues.archive", "queue.join", "queue.display",
  "messaging", "messaging.flows", "messaging.flow", "messaging.delivery", "messaging.compose",
  // Booking comms (APT-08) — RESTORED 2026-09-02 (operator ruling): the tab
  // left the tree unrecorded between 08-25 and 08-31 while the appointment-comms
  // lane it drives stayed live. Guarded on campaigns.read (2026-09-08).
  "messaging.appointments",
  // Full-screen email WYSIWYG for a library message (2026-07-30, Greg — messaging
  // overhaul step 2). Unguarded like messaging.compose; the screen itself
  // read-onlys without campaigns.write.
  "messaging.compose.email",
  // Messages, the two-tab surface (2026-09-11, studio-messaging-v1):
  // `messaging.automatic` is General (the automatic customer emails and their
  // switches), `messaging.settings` is Settings (the sender card). Guarded on
  // the capability each one's data actually needs — see ROUTE_READ_PERM below.
  "messaging.automatic", "messaging.settings",
  "guests.responses",
  "calendar.week", "calendar.month", "calendar.day",
  // The create.* wizard route family was REMOVED 2026-08-24 (operator: "sweep the
  // wizard now") — the Journey designer ("newjourneys") is the event create/edit
  // surface. hub.create.* below is the HUBS wizard, a separate surface, untouched.
  "hub.create.basics", "hub.create.page", "hub.create.publish", "hub.create.live",
  "guests", "guests.profile", "guests.import", "guests.import.map",
  "segments", "segments.detail", "segments.new", "waitlist", "invite",
  "campaigns", "campaigns.email", "campaigns.sms", "campaigns.notifications",
  "reports", "reports.pulse", "reports.events", "reports.event", "reports.library", "reports.detail",
  "settings.locations", "locations", "locations.edit", "locations.import",
  // The node model (JAI-930) — the tenant's organisation structure. A SEPARATE
  // surface from Studio Locations: two stores of overlapping real-world objects
  // with no reconciliation between them, and which one a future "places" screen
  // should own is an open product question. Locations is deliberately untouched.
  "nodes", "nodes.attributes", "settings.closures", "settings.import", "settings.import.status",
  "settings.integ", "settings.branding", "branding", "settings.calendar", "settings.manageLinks", "settings.approvals", "settings.ai",
  // Customer types (2026-09-08, kit e03f10e9). The screen, its Settings tab and
  // its capability family all shipped in that kit; only this id was missing, so
  // `screens.tsx` could not register the component it already imports.
  "settings.customerTypes",
  "approvals.process.edit", "approvals", "approvals.review", "settings.billing", "settings.tax", "orders", "account.details",
  "notifications", "checkins.today", "registrations.today", "vip.confirmations", "inbox",
  "landing.pages", "landing.editor", "hub.pages", "hub.editor",
  "hub.public.location", "hub.public.group", "hub.public.workspace", "event.public", "event.register",
  "events.archived", "guests.imports",
  "campaigns.sent", "campaigns.scheduled", "campaigns.drafts", "campaigns.approvals",
  "reports.guests", "reports.campaigns", "reports.revenue", "reports.appointments",
  "settings.general", "settings.security", "settings.audit", "settings.api", "settings.integ.detail",
  "help", "help.article",
  "surveys", "surveys.questions", "surveys.edit", "surveys.question.edit",
  "workforce", "workforce.schedule", "workforce.forecast", "workforce.coverage",
  "workforce.staffing", "workforce.staffing.event", "workforce.ops", "workforce.requests",
  "workforce.rules", "workforce.myday",
  // Working copy of My Day (2026-07-30) — same read guard as the original, below.
  "workforce.myday.sandbox",
  // Its day/week/month schedule (2026-07-31) — same read guard again.
  "workforce.myschedule.sandbox",
  // Merged "My Schedule & Time" (2026-07-31) — same read guard again.
  "workforce.scheduletime.sandbox",
  // My Skills & Services (2026-08-01) — same self-scope read guard.
  "workforce.myskills",
  "storeops", "storeops.floor", "storeops.insights", "storeops.clients", "storeops.events",
  "storeops.roster", "storeops.skills", "storeops.perf", "storeops.loyalty", "storeops.targets",
  "storeops.schedules", "storeops.staff", "storeops.integrations", "storeops.compliance",
  "storeops.settings", "storeops.region",
  "storeops.my.day", "storeops.my.book", "storeops.my.targets", "storeops.my.schedule",
  // Aperture Bank six-persona port (P1, 2026-07-17) — pending-port surfaces
  "workforce.setup", "workforce.copilot", "workforce.runs", "workforce.draft",
  "workforce.marketplace", "workforce.reporting", "workforce.payroll", "workforce.advisors",
  "workforce.compliance", "workforce.integration", "workforce.planning", "workforce.coaching",
  "workforce.assists", "workforce.requisitions",
  "workforce.region", "workforce.region.compare", "workforce.region.redistribute",
  "workforce.region.runs", "workforce.region.scope",
  "workforce.floor",
  /* Calendar insertion phase 2 (2026-08-08, Greg — docs/floor-calendar-port.md):
     the Floor Calendar day board. Own top-level section id, so neither the
     Workforce item nor a future Floor item double-lights (rail longest-match). */
  "floor.calendar",
  /* Calendar insertion, region layer (2026-08-10 — docs/floor-calendar-port.md):
     the Regional Calendar. Sibling section id to floor.calendar for the same
     rail longest-match reason. */
  "floor.region",
  "workforce.myperf", "workforce.myrelationships",
  "ops.today", "ops.walkins", "ops.services",
  /* REQ-128 (2026-08-03): the family-tree catalogue. `ops.catalogue` is Riley's configurable
     view; `workforce.services.view` is the SAME screen read-only for the branch manager and
     the advisor — gated on myschedule.read, deliberately NOT services.read, because an
     advisor has no catalogue-read capability and the point is that he can still see what
     his branch offers. */
  "ops.catalogue", "workforce.services.view", "workforce.coverage.staff",
  "journeys",
  // New Journeys (2026-08-17) — the unified Journey designer (plan v2 ruling ⑤:
  // ships as its OWN rail item; the existing queue/event/appointment editors are
  // sources only and unchanged).
  "newjourneys",
  // Confirmation pages (2026-08-31) — the post-booking page library + designer.
  // Operator placement: the SECOND TAB of the Journeys section, not its own rail
  // item, so it shares the New-journeys rail entry and capability.
  "confirmpages",
  // All Journeys (2026-09-03) — the Appointment Page Designer from the Gold Master
  // prototype, ported 1:1 as its OWN rail item under the Administrator profile of
  // every tenant. Sits BESIDE Journey and Journey Builder (operator: add, don't replace).
  "alljourneys",
  /* Customers (2026-09-10 drop). ONE directory surface: the searchable,
     sortable, paginated list and the linkable detail. `customers.detail`
     carries the id in the PATH (`/customers/<customerId>`), not the query —
     see router.tsx, which owns that one mapping. Both routes guard on
     `customers.read`; the 2026-07-24 port's "no read guard by design" note is
     retired with the screen it described. */
  "customers", "customers.detail",
] as const;

export type RouteId = (typeof ROUTE_IDS)[number];

/**
 * Minimum capability to LOAD a route — enforced centrally so a role that can't
 * see a surface can't deep-link into it. Action-level gates (write/publish/
 * delete/export) still live in the screens via can(). Routes absent here are
 * readable by any signed-in user.
 */
export const ROUTE_READ_PERM: Partial<Record<string, string>> = {
  // Queues process (2026-07-17 — queuing first pass; section-tab restructure
  // same day). `queue.join` + `queue.display` are the customer/lobby public
  // surfaces — deliberately unguarded.
  queues: "queues.read",
  "queues.console": "queue.serve",
  "queues.health": "queuehealth.read",
  "queues.dashboard": "queuehealth.read",
  "queues.boards": "queues.read",
  "queues.archive": "queues.read",
  // Appointments process (2026-07-26, APT-13). Gate ids are the APT_BRIDGE set
  // (see access.ts) until the canonical appointments.* catalog lands with the
  // auth wave. `appointment.manage` is the customer self-service page —
  // deliberately unguarded, like queue.join.
  appointments: "appointments.read",
  eventday: "onsite.walkup",
  /* Events section index (2026-08-06). Gated on the same read cap as the events
     list; the nine rows inside it carry their own per-surface gates. */
  services: "services.read",
  "services.edit": "services.read",
  "services.categories": "services.read",
  "services.category.edit": "services.read",
  "services.packages": "services.read",
  "services.package.edit": "services.read",
  products: "products.read",
  "products.edit": "products.read",
  surveys: "surveys.read",
  "surveys.questions": "surveys.read",
  "surveys.edit": "surveys.read",
  "surveys.question.edit": "surveys.read",
  "messaging.flows": "flows.read",
  "messaging.flow": "flows.read",
  "messaging.delivery": "delivery.read",
  /* Was `appointments.manage`, declared in NO catalog (corrected 2026-09-08) —
     so the ROUTE was unreachable for every persona, which is why the read-only
     Booking Settings looked like a permissions bug rather than a missing route.
     `campaigns.read` to open it; `ApptComms.tsx` gates its edit affordances on
     `campaigns.write`, which is the same split every other comms surface makes. */
  "messaging.appointments": "campaigns.read",
  /* General lists messages through `listStudioMessages`, which declares
     `campaigns.read`. Settings reads `GET /v1/admin/settings/messaging`, a
     different family entirely — guarding it on campaigns.read would hand a
     campaigns-only operator a tab whose every field the backend then refuses. */
  "messaging.automatic": "campaigns.read",
  "messaging.settings": "settings.read",
  inbox: "inbox.read",
  locations: "locations.read",
  "locations.edit": "locations.read",
  /* Bulk create (2026-08-07, Nick) — a CREATION surface, so it guards on
     locations.write rather than .read: a role that may look at the estate but
     not change it has no business deep-linking into the importer. */
  "locations.import": "locations.write",
  /* The node model (added 2026-09-09, CD docket §4). Both surfaces were absent
     from this map, and a route absent here is readable by any signed-in user —
     so a deep link reached the tenant's organisation structure with no
     capability at all. `locations.read` is the estate read cap: the nodes are
     the same real-world objects the Locations surfaces read, and no separate
     nodes.* family is declared in any catalog. */
  nodes: "locations.read",
  "nodes.attributes": "locations.read",
  // People (2026-08-06) — merged users + bookable staff. Same read gate as the
  // two surfaces it joins, so no persona gains reach it didn't already have.
  people: "team.read",
  /* The person page (2026-09-11, operator) — a roster row's nested record with
     its Personal / Roles / Availability / Absences tabs. Same gate as the
     roster it is reached from: it shows one row of what `people` already
     shows, so a deep link grants nothing new. */
  /* Roles split off the Staff screen 2026-09-10 (operator): role DEFINITION is
     System-Administrator-only, so it guards on `admin.super` — the capability
     only that role holds — not the `team.read` gate the roster keeps. */
  /* LOCAL PATCH (platform side, 2026-09-11): the `"people.person": "team.read"` and
     `roles: "admin.super"` entries the two comments above describe are HELD BACK with their
     route ids at the top of this file. They return verbatim with the Staff-and-Roles cut.
     Everything else in this file is byte-identical to Gold Master. */
  staff: "team.read",
  "staff.edit": "team.read",
  "staff.groups": "team.read",
  "staff.group.edit": "team.read",
  resources: "locations.read",
  "resources.edit": "locations.read",
  "resources.groups": "locations.read",
  "resources.group.edit": "locations.read",
  "settings.audit": "audit.read",
  "settings.api": "observability.read",
  "settings.security": "compliance.read",
  "settings.integ": "integrations.read",
  "settings.integ.detail": "integrations.read",
  "settings.billing": "billing.read",
  "settings.tax": "billing.read",
  orders: "billing.read",
  "settings.approvals": "settings.read",
  /* Its own capability family, not the blanket `settings.read` — the same gate
     the Settings tab declares (SettingsShell.tsx) and the one the customer-type
     operations actually check. Without this entry the route would be readable
     by any signed-in user, because routes absent from this map are ungated. */
  "settings.customerTypes": "customer_types.read",
  "settings.manageLinks": "settings.read",
  "settings.ai": "settings.read",
  "settings.branding": "branding.read",
  branding: "branding.read",
  // Workforce process (2026-07-16 scaffold — spec: specs/workforce-management-spec-2026-07-16.md)
  workforce: "schedules.read",
  "workforce.schedule": "schedules.read",
  "workforce.forecast": "demand.read",
  "workforce.coverage": "demand.read",
  "workforce.staffing": "staffing.read",
  "workforce.staffing.event": "staffing.read",
  "workforce.ops": "wfops.read",
  "workforce.requests": "swaps.read",
  "workforce.rules": "laborrules.read",
  "workforce.myday": "myschedule.read",
  "workforce.myday.sandbox": "myschedule.read",
  "workforce.myschedule.sandbox": "myschedule.read",
  "workforce.scheduletime.sandbox": "myschedule.read",
  "workforce.myskills": "myschedule.read",
  // Store Ops process (2026-07-16 — Maison Rivière fidelity port)
  storeops: "storeops.read",
  "storeops.floor": "storeops.read",
  "storeops.insights": "storeops.read",
  "storeops.clients": "storeops.read",
  "storeops.events": "storeops.read",
  "storeops.roster": "storeops.floorplan",
  "storeops.skills": "storeops.floorplan",
  "storeops.perf": "storeops.floorplan",
  "storeops.loyalty": "storeops.read",
  "storeops.targets": "storeops.read",
  "storeops.schedules": "storeops.floorplan",
  "storeops.staff": "storeops.read",
  "storeops.integrations": "storeops.read",
  "storeops.compliance": "storeops.read",
  "storeops.settings": "storeops.manage",
  "storeops.region": "storeops.region",
  "storeops.my.day": "mybook.read",
  "storeops.my.book": "mybook.read",
  "storeops.my.targets": "mybook.read",
  "storeops.my.schedule": "mybook.read",
  // Aperture Bank six-persona port (P1, 2026-07-17)
  "workforce.setup": "settings.read",
  "workforce.copilot": "copilot.run",
  "workforce.runs": "runs.read",
  "workforce.draft": "schedules.publish",
  "workforce.marketplace": "swaps.read",
  "workforce.reporting": "wfreports.read",
  "workforce.payroll": "payroll.read",
  "workforce.advisors": "advisors.read",
  "workforce.compliance": "compliance.read",
  "workforce.integration": "integrations.read",
  "workforce.planning": "hrplanning.read",
  "workforce.coaching": "coaching.read",
  "workforce.assists": "assists.read",
  "workforce.requisitions": "requisitions.read",
  "workforce.region": "region.read",
  "workforce.region.compare": "region.read",
  "workforce.region.redistribute": "region.read",
  "workforce.region.runs": "region.read",
  "workforce.region.scope": "region.read",
  // Sarah's Floor board (P2 port, 2026-07-20 — spec §3.11): its own route so the
  // shared workforce.ops board keeps serving the generic tenants unchanged.
  "workforce.floor": "wfops.read",
  // Calendar insertion phase 2 (2026-08-08, Greg): same floor-ops read gate.
  "floor.calendar": "wfops.read",
  /* Region layer (2026-08-10): the regional manager's own read cap — which is
     also the persona gate. A store manager holds wfops.read but not region.read,
     so the Floor board is hers and the region board is not. */
  "floor.region": "region.read",
  "workforce.myperf": "myschedule.read",
  "workforce.myrelationships": "myschedule.read",
  "ops.today": "events.read",
  "ops.walkins": "onsite.queue",
  "ops.services": "services.read",
  "ops.catalogue": "services.read",
  "workforce.services.view": "myschedule.read",
  /* The branch manager configures her team's service sign-off here (2026-08-03); advisors.read is
     the manager-scoped capability the Advisors screen already uses. */
  "workforce.coverage.staff": "advisors.read",
  // The journeys.* capability family exists specifically for this surface, so the
  // Journey builder gates on journeys.read rather than the general events read cap.
  // This is a TIGHTENING: previously the only way to open Journey was to hold the
  // whole events read surface, which the Operations Admin persona must not hold.
  // System Administrator holds every capability and is unaffected; the events
  // manager bundle carries no journeys.* cap, matching the canonical role model.
  journeys: "journeys.read",
  // The three designer surfaces gate on `journeys.read`, the same family as
  // `journeys` above (corrected 2026-09-08). They previously named
  // `newjourneys.read`, which NO CATALOG DECLARES — an undeclared capability is
  // not an admin-only gate, it is a gate no persona can ever pass, so all three
  // surfaces were unreachable for every role except whole-catalog System
  // Administrator by accident rather than by ruling. `journeys.read` is the
  // family that exists and is enforced; the designer is not a different kind of
  // thing from the journeys it designs.
  newjourneys: "journeys.read",
  confirmpages: "manage_pages.read",
  alljourneys: "journeys.read",
  /* The directory and its detail read the same records, so they take the same
     gate. Visibility never grants access: the backend remains authoritative,
     and every operation on those screens is separately enforced
     (`customer_types.read` for names, `customers.write` to assign,
     `customers.service_record.read` for history, `settings.write` to edit the
     shared column layout). */
  customers: "customers.read",
  "customers.detail": "customers.read",
};
